import { feedbackDiagnostics, recentErrors, type BuildInfo, type DeviceFacts, type FeedbackDiagnostics } from '@higgins/utils';

/**
 * The build the deploy workflow stamped (VITE_APP_*). Read here, once, rather than passed in by
 * every app: Vite replaces `import.meta.env` in this package's source when it builds the app.
 */
export function buildInfo(): BuildInfo {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    version: env.VITE_APP_VERSION,
    commit: env.VITE_APP_COMMIT,
    branch: env.VITE_APP_BRANCH,
    builtAt: env.VITE_APP_BUILT_AT,
  };
}

/** True when the app was opened from a home-screen icon rather than a browser tab. */
function isHomeScreenApp(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

function deviceFacts(path: string): DeviceFacts {
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    landscape: window.innerWidth > window.innerHeight,
    homeScreenApp: isHomeScreenApp(),
    online: navigator.onLine,
    path,
  };
}

/** What can be known about where the user was when they pressed Send (FEEDBACK-PLAN.md §11.2). */
export async function captureDiagnostics(options: {
  screenName?: string;
  changesToSync?: () => Promise<number>;
  /**
   * The app the report is about, when it isn't this one (App Feedback's report page for an app
   * with no API): its build and page. This tab's recent errors are then left out, being App
   * Feedback's own, not that app's.
   */
  otherApp?: { build: BuildInfo; path: string };
}): Promise<FeedbackDiagnostics> {
  // A count that can't be read is left out: it must never stop the report.
  const changesToSync = options.changesToSync ? await options.changesToSync().catch(() => undefined) : undefined;
  return feedbackDiagnostics({
    facts: deviceFacts(options.otherApp?.path ?? window.location.pathname),
    build: options.otherApp?.build ?? buildInfo(),
    recentErrors: options.otherApp ? { shown: [], failedCalls: [] } : recentErrors(),
    screenName: options.screenName,
    changesToSync,
  });
}
